import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useContactSettings } from '@/hooks/useContactSettings';
import { usePosts } from '@/hooks/usePosts';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import Seo from '@/components/base/Seo';

const CONTACT_FORM_URL = 'https://nsordyufacdmsmnpubhr.supabase.co/functions/v1/contact-form';

export default function ContactPage() {
  const { settings } = useContactSettings();
  const { getPublishedPosts } = usePosts();
  const allPosts = getPublishedPosts();

  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;

    setSubmitState('submitting');

    try {
      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        }),
      });

      if (!response.ok) throw new Error('Erro ao enviar');

      setSubmitState('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch {
      setSubmitState('error');
    }
  };

  const phoneHref = settings.phoneNumber
    ? settings.phoneIcon.includes('whatsapp')
      ? `https://wa.me/55${settings.phoneNumber.replace(/\D/g, '')}`
      : `tel:+55${settings.phoneNumber.replace(/\D/g, '')}`
    : '';

  return (
    <div className="min-h-screen bg-background-50 font-body">
      <Seo title="Contato" description="Entre em contato com a equipe do Miau de Casa para enviar dúvidas, sugestões ou falar sobre nosso conteúdo sobre gatos." path="/contato" />
      <Navbar />

      {/* Hero */}
      <section className="w-full bg-background-50 pt-20 md:pt-24 pb-8 md:pb-12">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <nav className="flex items-center justify-center gap-2 mb-4 text-foreground-400 text-xs">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">Início</Link>
            <i className="ri-arrow-right-s-line text-sm"></i>
            <span className="text-foreground-600">{settings.pageTitle}</span>
          </nav>
          <h1 className="font-heading font-black text-foreground-950 text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight mb-3 md:mb-4 animate-fade-in-up">
            {settings.pageTitle}
          </h1>
          <p className="text-foreground-500 text-sm md:text-lg max-w-2xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'both' }}>
            {settings.pageSubtitle}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="w-full bg-background-50 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-10 md:gap-16">
            {/* Left — info */}
            <div className="lg:w-[380px] flex-shrink-0 space-y-8">
              <div>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8">
                  {settings.description}
                </p>

                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                      <i className="ri-mail-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-foreground-500 uppercase tracking-wide mb-0.5">E-mail</p>
                      <a href={`mailto:${settings.emailAddress}`} className="text-foreground-700 text-sm hover:text-primary-500 transition-colors cursor-pointer">
                        {settings.emailAddress}
                      </a>
                    </div>
                  </div>

                  {settings.phoneNumber && (
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                        <i className={`${settings.phoneIcon} text-accent-500 text-lg`}></i>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-foreground-500 uppercase tracking-wide mb-0.5">{settings.phoneLabel}</p>
                        <a
                          href={phoneHref}
                          target={settings.phoneIcon.includes('whatsapp') ? '_blank' : undefined}
                          rel={settings.phoneIcon.includes('whatsapp') ? 'nofollow noopener noreferrer' : undefined}
                          className="text-foreground-700 text-sm hover:text-primary-500 transition-colors cursor-pointer"
                        >
                          {settings.phoneNumber.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3').replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3')}
                        </a>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 flex-shrink-0">
                      <i className="ri-time-line text-secondary-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-foreground-500 uppercase tracking-wide mb-0.5">Tempo de resposta</p>
                      <p className="text-foreground-700 text-sm">{settings.responseTime}</p>
                    </div>
                  </div>
                </div>

                {/* Custom Sections */}
                {settings.customSections.length > 0 && (
                  <div className="mt-8 pt-8 border-t border-background-200 space-y-5">
                    {settings.customSections.map((section, i) => (
                      <div key={i}>
                        <h3 className="text-xs font-bold text-foreground-500 uppercase tracking-wide mb-1.5">{section.title}</h3>
                        <p className="text-foreground-600 text-sm leading-relaxed whitespace-pre-line">{section.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Image */}
              {settings.imageUrl && (
                <div className="hidden lg:block w-full aspect-[4/3] rounded-2xl overflow-hidden">
                  <img
                    src={settings.imageUrl}
                    alt={settings.pageTitle}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              )}
            </div>

            {/* Right — form */}
            <div className="flex-1">
              <div className="bg-background-100 border border-background-200 rounded-2xl p-6 md:p-8 lg:p-10">
                {submitState === 'success' ? (
                  <div className="text-center py-12 animate-fade-in-up">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
                      <i className="ri-check-line text-green-500 text-2xl"></i>
                    </div>
                    <h3 className="font-heading font-bold text-foreground-950 text-xl mb-2">Mensagem enviada!</h3>
                    <p className="text-foreground-500 text-sm mb-6">Obrigado pelo contato. Responderemos em breve.</p>
                    <button
                      onClick={() => setSubmitState('idle')}
                      className="bg-primary-500 hover:bg-primary-600 text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Enviar outra mensagem
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
                          Nome *
                        </label>
                        <input
                          id="contact-name"
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Seu nome"
                          className="w-full border border-background-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white transition-colors"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
                          E-mail *
                        </label>
                        <input
                          id="contact-email"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="seu@email.com"
                          className="w-full border border-background-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="contact-subject" className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
                        Assunto *
                      </label>
                      <select
                        id="contact-subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full border border-background-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white cursor-pointer transition-colors"
                      >
                        <option value="">Selecione um assunto...</option>
                        <option value="duvida">Dúvida sobre conteúdo</option>
                        <option value="sugestao">Sugestão de artigo</option>
                        <option value="parceria">Proposta de parceria</option>
                        <option value="erro">Reportar erro no site</option>
                        <option value="outro">Outro assunto</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
                        Mensagem *
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        maxLength={500}
                        placeholder="Escreva sua mensagem aqui..."
                        className="w-full border border-background-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white resize-none transition-colors"
                      />
                      <p className="text-xs text-foreground-400 text-right mt-1">{formData.message.length}/500</p>
                    </div>

                    {submitState === 'error' && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                        <i className="ri-error-warning-line text-red-500"></i>
                        <p className="text-red-600 text-xs">Erro ao enviar. Tente novamente ou envie um e-mail diretamente para {settings.emailAddress}.</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitState === 'submitting'}
                      className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-bold text-sm py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      {submitState === 'submitting' ? (
                        <><i className="ri-loader-4-line animate-spin"></i> Enviando...</>
                      ) : (
                        <><i className="ri-send-plane-fill"></i> Enviar mensagem</>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer popularPosts={allPosts} />
    </div>
  );
}
